<template>
  <section v-if="drafts.length" class="agent-reply-draft" aria-label="AI suggested reply">
    <AgentActionCard
      v-for="action in drafts"
      :key="action.id"
      :action="action"
      :busy="busyIds.has(action.id)"
      :now="now"
      title="AI suggested reply"
      approve-label="Send"
      reject-label="Dismiss"
      quick-reject
      @approve="(payload) => approve(action, payload)"
      @reject="(reason) => dismiss(action, reason ?? DISMISSED)"
    >
      <template #actions="{ busy }">
        <Button
          v-if="showOpen && replyToEmailIdOf(action)"
          variant="outline"
          size="sm"
          :disabled="busy"
          @click="emit('open', replyToEmailIdOf(action)!)"
        >
          Open email
        </Button>
        <!-- The thread list has no reply box: offering "Use" there would only dismiss the draft. -->
        <Button
          v-else-if="!showOpen"
          variant="outline"
          size="sm"
          :disabled="busy"
          @click="useInReplyBox(action)"
        >
          Use in reply box
        </Button>
      </template>
    </AgentActionCard>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type { Socket } from "socket.io-client";
import { Button } from "@/components/ui";
import AgentActionCard from "@/components/agent/AgentActionCard.vue";
import AgentService from "@/services/ai/agent";
import { useAgentActions } from "@/composables/agent/useAgentActions";
import { getSocket } from "@/utils/socket";
import type { AgentAction, AgentReplyDraftEvent } from "@/dtos/Agent.dto";

// The AI's suggested answer to a contact's latest email or LinkedIn message, shown in the
// conversation. The rep sends it as is, edits it, takes it into the reply box to rewrite,
// or dismisses it. Drafts are written in the background when a message arrives
// (reply-drafts.ts on the server) and announced over the socket.

const props = defineProps<{
  contactId?: string | null;
  channel: "email" | "linkedin";
  /** Email only: show just the drafts that answer one of these emails (the open thread's). */
  emailIds?: string[];
  /** Offer "Open email" instead of "Use in reply box", for the thread list. */
  showOpen?: boolean;
}>();

const emit = defineEmits<{
  /** The draft, moved to the reply box: `text` is HTML for email, plain text for LinkedIn. */
  use: [draft: { text: string; subject?: string; replyToEmailId?: string }];
  /** Open the email thread the draft answers. */
  open: [emailId: string];
}>();

const REPLY_DRAFT_EVENT = "agent:reply-draft";
const DISMISSED = "Dismissed";
const MOVED_TO_REPLY_BOX = "Moved to the reply box";

const queryClient = useQueryClient();
const queryKey = computed(() => ["agent-reply-drafts", props.contactId]);

const { data } = useQuery({
  queryKey,
  queryFn: async (): Promise<AgentAction[]> => {
    const result = await AgentService.listActions({
      status: "pending",
      contactId: props.contactId!,
      source: "reply",
      limit: 10,
    });
    return result.data?.data ?? [];
  },
  enabled: computed(() => !!props.contactId),
});

const replyToEmailIdOf = (action: AgentAction): string | undefined =>
  (action.editedPayload ?? action.payload)?.replyToEmailId;

const drafts = computed(() =>
  (data.value ?? []).filter((action) => {
    if (props.channel === "linkedin") return action.actionType === "send_linkedin_message";
    if (action.actionType !== "send_email") return false;
    if (!props.emailIds) return true;
    const replyTo = replyToEmailIdOf(action);
    return !!replyTo && props.emailIds.includes(replyTo);
  })
);

// Keeps the card's status in step with each decision: "Sent", "Failed"…
const updateAction = (action: AgentAction) =>
  queryClient.setQueryData<AgentAction[]>(queryKey.value, (list) =>
    list?.map((a) => (a.id === action.id ? { ...action, contact: action.contact ?? a.contact } : a))
  );
const { busyIds, approve, reject } = useAgentActions(updateAction);

/** Rejects the draft and takes its card away; nothing more to show for it. */
const dismiss = async (action: AgentAction, reason: string) => {
  if (!(await reject(action, reason))) return;
  queryClient.setQueryData<AgentAction[]>(queryKey.value, (list) =>
    list?.filter((a) => a.id !== action.id)
  );
};

const useInReplyBox = (action: AgentAction) => {
  const input = action.editedPayload ?? action.payload;
  emit("use", {
    text: action.actionType === "send_email" ? input.body : input.text,
    subject: input.subject,
    replyToEmailId: input.replyToEmailId,
  });
  // The rep owns the text now; the draft itself is done with.
  return dismiss(action, MOVED_TO_REPLY_BOX);
};

// Ticks so a draft left open shows when it expires.
const now = ref(Date.now());
const timer = window.setInterval(() => (now.value = Date.now()), 30_000);

const onDraft = (event: AgentReplyDraftEvent) => {
  if (event?.contactId === props.contactId) {
    queryClient.invalidateQueries({ queryKey: queryKey.value });
  }
};

// Live updates are a nicety: drafts still load with the conversation without the socket.
let socket: Socket | null = null;
try {
  socket = getSocket();
  socket.on(REPLY_DRAFT_EVENT, onDraft);
} catch (err) {
  console.error("AI reply draft updates are unavailable:", err);
}

onBeforeUnmount(() => {
  window.clearInterval(timer);
  socket?.off(REPLY_DRAFT_EVENT, onDraft);
});
</script>

<style scoped>
.agent-reply-draft {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
  margin: 8px 0;
  /* Under a conversation, the draft mustn't push the message box out of view. */
  max-height: 45vh;
  overflow-y: auto;
}
</style>
